"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModalOverlay = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const BasicButtonClose_1 = require("@jsonjoy.com/ui/lib/2-inline-block/BasicButton/BasicButtonClose");
const ScrollArea = tslib_1.__importStar(require("@jsonjoy.com/ui/lib/4-card/ScrollArea"));
const ScrollArea_1 = require("@jsonjoy.com/ui/lib/4-card/ScrollArea");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const overlayClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    top: '0',
    l: '0',
    r: '0',
    b: '0',
    z: 50,
    d: 'flex',
    fld: 'column',
    animation: 'fadeIn .12s',
});
const HEADER_HEIGHT = 48;
const headerClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    d: 'flex',
    ai: 'center',
    jc: 'flex-end',
    h: HEADER_HEIGHT + 'px',
    paddingBlock: 0,
    paddingInlineStart: '12px',
    paddingInlineEnd: '12px',
    bxz: 'border-box',
    '-webkit-app-region': 'drag', // Drag for Electron app.
    '& button, & a, & input, & textarea, & select, & [role="button"], & [role="tablist"], & [role="img"]': {
        '-webkit-app-region': 'no-drag',
    },
});
const titleClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    l: '50%',
    tr: 'translateX(-50%)',
    fz: '14px',
    fw: '600',
    letterSpacing: '0.005em',
    ws: 'nowrap',
    pointerEvents: 'none',
});
/**
 * A modal that overlays the editor area.
 */
const ModalOverlay = ({ open, title, onClose, children }) => {
    const styles = (0, context_1.useStyles)();
    const theme = (0, nano_theme_1.useTheme)();
    const onCloseRef = React.useRef(onClose);
    React.useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);
    const scrollState = React.useMemo(() => new ScrollArea_1.ScrollState(), []);
    React.useEffect(() => {
        if (!open)
            return;
        const onKey = (event) => {
            if (event.key !== 'Escape')
                return;
            event.preventDefault();
            event.stopPropagation();
            onCloseRef.current();
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [open]);
    // When the modal opens, programmatically focus the scrollable viewport so
    // the user can scroll with arrow keys / Space / PgUp / PgDn immediately.
    React.useEffect(() => {
        if (!open)
            return;
        const previouslyFocused = document.activeElement;
        const id = requestAnimationFrame(() => {
            scrollState.viewportEl?.focus({ preventScroll: true });
        });
        return () => {
            cancelAnimationFrame(id);
            if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
                try {
                    previouslyFocused.focus({ preventScroll: true });
                }
                catch { }
            }
        };
    }, [open, scrollState]);
    if (!open)
        return null;
    const overlayBg = theme.bg;
    const borderColor = styles.g(0, 0.08);
    return (React.createElement("div", { className: overlayClass, style: { background: overlayBg }, role: "dialog", "aria-modal": "true" },
        React.createElement(ScrollArea.ScrollArea, { state: scrollState, shadowFlat: true },
            React.createElement(ScrollArea.Header, null,
                React.createElement("div", { className: headerClass, style: { background: overlayBg, borderBottom: `1px solid ${borderColor}` } },
                    React.createElement("span", { className: titleClass, style: { color: styles.g(0.1, 0.95) } }, title),
                    React.createElement(BasicButtonClose_1.BasicButtonClose, { rounder: true, onClick: onClose, title: "Close (Esc)" }))),
            React.createElement(ScrollArea.Viewport, { tabIndex: -1, style: { outline: 'none' } }, children),
            React.createElement(ScrollArea.ScrollRail, null,
                React.createElement(ScrollArea.Thumb, null)))));
};
exports.ModalOverlay = ModalOverlay;
